import {useCallback, useEffect, useState} from 'react';
import {useIsMounted} from 'usehooks-ts';

export async function copyToClipboard(text: string) {
    if (!navigator?.clipboard) {
    // eslint-disable-next-line no-console
        console.warn('Clipboard not supported');

        return false;
    }

    // Try to save to clipboard
    try {
        await navigator.clipboard.writeText(text);

        return true;
    } catch (error) {
    // eslint-disable-next-line no-console
        console.warn('Copy failed', error);

        return false;
    }
}

export function useCopyToClipboardFunc() {
    return useCallback(copyToClipboard, []);
}

const SUCCESSFUL_COPY_TIMEOUT = 500; // 0.5s
const FAILED_COPY_TIMEOUT = 1500; // 1.5s

/**
 * Копирует в буфер обмена с состоянием
 */
export function useCopyToClipboardStateful(clipboardValue: string, resetStateOnTimeout = true) {
    const [state, setState] = useState<'init' | 'pending' | 'success' | 'failed'>('init');
    const isMounted = useIsMounted();

    useEffect(() => {
        const shouldResetState = state === 'failed' || state === 'success';

        const timeout = state === 'failed' ? FAILED_COPY_TIMEOUT : SUCCESSFUL_COPY_TIMEOUT;

        if (shouldResetState && resetStateOnTimeout) {
            setTimeout(() => {
                if (!isMounted()) {
                    return;
                }

                setState('init');
            }, timeout);
        }
    }, [state, resetStateOnTimeout, isMounted]);

    const handleCopy = useCallback(async () => {
        setState('pending');
        const result = await copyToClipboard(clipboardValue);

        if (!isMounted()) {
            return;
        }

        if (result === true) {
            setState('success');
        } else {
            setState('failed');
        }
    }, [clipboardValue, isMounted]);

    return {
        handleCopy,
        state,
        successful: state === 'success',
        failed: state === 'failed',
        pending: state === 'pending',
        init: state === 'init',
    };
}
